import { ActionButton } from '@/components/ActionButton';
import { IconName } from '@fortawesome/pro-solid-svg-icons';
import { Pagination } from '@/components/Pagination';
import { ThEl } from '@/features/Quiz/components/ArticlesQuiz';
import { useLibraryQuizListV2 } from '@/features/Quiz/api/libraryQuizListV2';
import type { LibraryQuizListResultV2 } from '@/features/Quiz/api/libraryQuizListV2';
import {
    rebuildChapterQuizLibraryV2,
    rebuildChapterQuizByIdV2,
} from '@/features/Quiz/api/rebuildLibraryQuizV2';
import type { ChapterQuizRebuildScope, ChapterQuizRebuildMode } from '@/features/Quiz/api/rebuildLibraryQuizV2';
import { isSourceLocale, libraryQuizSyncStatusPath } from '@/features/Quiz/api/syncLibraryQuizV2';
import { DeleteQuizModal } from '@/features/Quiz/components/DeleteQuizModal';
import { LocaleFilter } from '@/features/Quiz/components/LocaleFilter';
import { useNotificationStore } from '@/stores/notifications';
import { debounce } from '@/utils/helpers';
import { libraryRebuildLocales } from '@/utils/consts';
import { useAdminQuizV2Cc } from '@/api/ccApi';
import InstitutionsModal from '@/features/Playlists/components/InstitutionsModal';
import { InstitutionModalTypes } from '@/features/Playlists';
import { useAdminQuizV2Scorm } from '@/api/scormApi';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Radio,
    RadioGroup,
    Select,
    Spinner as SpinnerChakra,
    Tab,
    TabList,
    Table,
    Tabs,
    Tbody,
    Td,
    Text,
    Thead,
    Tr,
    useDisclosure,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import Icon from '@/components/Icon';
import React, { ChangeEvent, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaSearch } from 'react-icons/fa';
import { HiX } from 'react-icons/hi';

const PAGE_SIZE = 20;

type SearchFormData = { keyword: string };

const SCOPE_OPTIONS: { value: ChapterQuizRebuildScope; label: string }[] = [
    { value: 'ALL', label: 'Entire Quiz Library' },
    { value: 'CORE', label: 'Core' },
    { value: 'SCIENCE_EDUCATION', label: 'Science Education' },
    { value: 'LAB_MANUAL', label: 'Lab Manual' },
    { value: 'BUSINESS', label: 'Business' },
];

type RebuildFormData = { scope: ChapterQuizRebuildScope; locale: string; mode: ChapterQuizRebuildMode };

const rebuildSchema = yup.object().shape({
    scope: yup.string().required('This field is required'),
    locale: yup.string().required('This field is required'),
    mode: yup.string().required('This field is required'),
});

type RebuildQuizModalV2Props = {
    isOpen: boolean;
    onClose: () => void;
};

const RebuildQuizModalV2 = ({ isOpen, onClose }: RebuildQuizModalV2Props) => {
    const [isLoading, setIsLoading] = useState(false);
    const { addNotification } = useNotificationStore();

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: { errors },
    } = useForm<RebuildFormData>({
        mode: 'onChange',
        resolver: yupResolver(rebuildSchema) as any,
        defaultValues: { scope: undefined as any, locale: undefined as any, mode: 'ADD_MISSING' },
    });

    const onSubmit = async (data: RebuildFormData) => {
        setIsLoading(true);
        try {
            await rebuildChapterQuizLibraryV2({ scope: [data.scope], mode: data.mode, locale: data.locale });
            addNotification({ type: 'info', title: 'Chapter quiz rebuild has been added to the queue' });
            onClose();
            reset();
        } catch (e: any) {
            addNotification({ type: 'error', title: e?.message ?? 'Rebuild failed' });
        }
        setIsLoading(false);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalHeader>Rebuild Chapter Quiz Library</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody mx={4}>
                        <FormControl isInvalid={!!errors.scope} mb={4}>
                            <Select placeholder="Select the section to Rebuild" {...register('scope')}>
                                {SCOPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Select>
                            <FormErrorMessage>{errors.scope?.message}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors.locale} mb={4}>
                            <Select placeholder="Select the locale to Rebuild" {...register('locale')}>
                                {libraryRebuildLocales.map((opt) => (
                                    <option key={opt.key} value={opt.key}>{opt.name}</option>
                                ))}
                            </Select>
                            <FormErrorMessage>{errors.locale?.message}</FormErrorMessage>
                        </FormControl>

                        {!!watch('scope') && !!watch('locale') && (
                            <RadioGroup
                                value={watch('mode')}
                                onChange={(v) => setValue('mode', v as ChapterQuizRebuildMode)}>
                                <Radio value="ADD_MISSING" colorScheme="blue" mr="15px">
                                    Add Only Missing Quizzes
                                </Radio>
                                <Radio value="REBUILD_ALL" colorScheme="blue">
                                    Rebuild All Quizzes
                                </Radio>
                            </RadioGroup>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button mr={3} onClick={onClose} isDisabled={isLoading}>Close</Button>
                        <Button colorScheme="blue" type="submit" isLoading={isLoading}>
                            Rebuild Quiz Library
                        </Button>
                    </ModalFooter>
                </form>
            </ModalContent>
        </Modal>
    );
};

const QuizLibraryItemV2 = ({
    result,
    isSectionType,
}: {
    result: LibraryQuizListResultV2;
    isSectionType: boolean;
}) => {
    const quizUuid = result.quiz.uuid;
    const subjects = isSectionType
        ? result.filters.collection_names?.join(', ') || '-'
        : result.filters.subject_names?.join(', ') || '-';
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isInstitutionsOpen, onOpen: onInstitutionsOpen, onClose: onInstitutionsClose } = useDisclosure();
    const [type, setType] = useState<InstitutionModalTypes>(null);
    const [isRebuilding, setIsRebuilding] = useState(false);
    const { addNotification } = useNotificationStore();

    // English is the source every other language is generated from, so only it has
    // anything to sync. Offering the action on a translation would ask the server to
    // translate text that is already a translation.
    const isSource = isSourceLocale(result.library?.locale);

    const { isLoading: isCCLoading, handleCCDownload } = useAdminQuizV2Cc({
        quizUuid,
        title: result.quiz.title ?? '',
    });

    const { isLoading: isScormLoading, handleScormDownload } = useAdminQuizV2Scorm({
        quizUuid,
        playlistTitle: result.quiz.title ?? '',
    });

    const handleOpenInstitutionsCC = () => {
        setType('cc');
        onInstitutionsOpen();
    };

    const handleOpenInstitutionsScorm = () => {
        setType('scorm');
        onInstitutionsOpen();
    };

    const handlePerRowRebuild = async () => {
        if (!quizUuid) return;
        setIsRebuilding(true);
        try {
            await rebuildChapterQuizByIdV2(quizUuid);
            addNotification({ type: 'info', title: 'Chapter quiz rebuild triggered' });
        } catch (e: any) {
            addNotification({ type: 'error', title: e?.message ?? 'Rebuild failed' });
        }
        setIsRebuilding(false);
    };

    const actionButtons = [
        {
            link: `/quiz/library/edit/${quizUuid}`,
            tooltip: 'Edit',
            icon: 'pencil',
        },
        {
            link: `/quiz/library/copy/${quizUuid}`,
            tooltip: 'Copy',
            icon: 'clone',
        },
        {
            tooltip: 'Download SCORM',
            icon: 'file-zipper',
            handleClick: handleOpenInstitutionsScorm,
        },
        {
            tooltip: 'Download CC',
            icon: 'closed-captioning',
            handleClick: handleOpenInstitutionsCC,
            isLoading: isCCLoading,
        },
        ...(isSectionType
            ? [
                  {
                      tooltip: 'Rebuild quiz',
                      icon: 'trowel-bricks',
                      handleClick: handlePerRowRebuild,
                      isLoading: isRebuilding,
                  },
              ]
            : []),
        ...(isSource
            ? [
                  {
                      tooltip: 'Sync',
                      icon: 'arrows-rotate',
                      link: libraryQuizSyncStatusPath(quizUuid),
                  },
              ]
            : []),
        {
            tooltip: 'Delete',
            icon: 'trash',
            handleClick: onOpen,
        },
    ];

    return (
        <>
            <InstitutionsModal
                isOpen={isInstitutionsOpen}
                onClose={onInstitutionsClose}
                handleScormDownload={handleScormDownload}
                handleCCDownload={handleCCDownload}
                handleQtiDownload={() => undefined}
                isLoading={isScormLoading || isCCLoading}
                type={type}
            />
            <DeleteQuizModal
                isOpen={isOpen}
                onClose={onClose}
                quiz={{ quizUuid, title: result.quiz.title ?? '' }}
                quizType="libraryV2"
            />
            {/* @ts-expect-error Expression produces a union type that is too complex to represent. */}
            <Tr>
                <Td w="65%" py="10px">
                    <Flex as={Link} to={`/quiz/library/view/${quizUuid}`} py="10px">
                        <Text color="black">{result.quiz.title}</Text>
                    </Flex>
                </Td>
                <Td w="10%">
                    <Text color="black">{subjects}</Text>
                </Td>
                <Td w="auto" maxWidth="260px">
                    <Flex columnGap="10px" align="center" justifyContent="space-around">
                        {/* Copy Quiz action intentionally hidden.
                        Duplicating a quiz offers limited value since it copies the same questions/videos. */}
                        {actionButtons.map(({ icon, handleClick, tooltip, link, isLoading }) =>
                            tooltip === 'Copy' ? null : (
                                <ActionButton
                                    key={tooltip}
                                    linkTo={link}
                                    isInProcess={false}
                                    tooltipTitle={tooltip}
                                    handleClick={handleClick}
                                    isLoading={isLoading}
                                    icon={icon as IconName}
                                />
                            ),
                        )}
                    </Flex>
                </Td>
            </Tr>
        </>
    );
};

export const QuizLibraryV2 = () => {
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [libraryType, setLibraryType] = useState<'TEXTBOOK' | 'CHAPTER'>('TEXTBOOK');
    const [tabIndex, setTabIndex] = useState(0);
    const [selectedLocale, setSelectedLocale] = useState('');
    const { isOpen: isRebuildOpen, onOpen: onRebuildOpen, onClose: onRebuildClose } = useDisclosure();

    const { register, reset, watch } = useForm<SearchFormData>({
        defaultValues: { keyword: '' },
    });

    const { data, isFetching } = useLibraryQuizListV2({
        page,
        size: PAGE_SIZE,
        search: search || undefined,
        filters: {
            libraryType,
            ...(selectedLocale ? { locale: selectedLocale } : {}),
        },
    });

    const results = data?.results ?? [];
    const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;

    const applySearch = useCallback(
        (keyword: string) => {
            setPage(1);
            setSearch(keyword);
        },
        []
    );

    const onSearchChange = debounce(
        useCallback(
            (e: ChangeEvent<HTMLInputElement>) => {
                applySearch(e.target.value);
            },
            [applySearch]
        ),
        300
    );

    const handleReset = useCallback(() => {
        reset();
        applySearch('');
    }, [reset, applySearch]);

    const handleLocaleChange = useCallback((locale: string) => {
        setPage(1);
        setSelectedLocale(locale);
    }, []);

    const handleTabsChange = (index: number) => {
        setTabIndex(index);
        setLibraryType(index === 0 ? 'TEXTBOOK' : 'CHAPTER');
        setPage(1);
        setSearch('');
        reset();
    };

    return (
        <Flex w="100%" flexDir="column" mb="10px">
            <RebuildQuizModalV2 isOpen={isRebuildOpen} onClose={onRebuildClose} />

            <Tabs index={tabIndex} onChange={handleTabsChange} mb={5}>
                <TabList>
                    <Tab fontSize="20px" color="#777" fontWeight="500">
                        Textbooks
                    </Tab>
                    <Tab fontSize="20px" color="#777" fontWeight="500">
                        Sections
                    </Tab>
                </TabList>
            </Tabs>

            <Flex w="100%">
                <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                    w="100%"
                    gap="8px"
                    wrap="wrap">
                    <Flex gap="8px" alignItems="center" flex="1 1 0" minW={0} wrap="wrap">
                        <InputGroup minW="10rem" maxW="16rem" flex="1 1 12rem" className="input-left-wrap">
                            <InputLeftElement
                                className="InputLeft"
                                pointerEvents="auto">
                                {isFetching ? <SpinnerChakra size="20px" /> : <FaSearch size="20px" />}
                            </InputLeftElement>
                            <Input
                                placeholder="Search JoVE Library"
                                bg="#ffffff"
                                id="keyword"
                                type="text"
                                borderRadius="1000px"
                                {...register('keyword')}
                                onChange={onSearchChange}
                            />
                            {watch('keyword') && (
                                <InputRightElement cursor="pointer" onClick={handleReset}>
                                    <HiX />
                                </InputRightElement>
                            )}
                        </InputGroup>
                        <LocaleFilter value={selectedLocale} onChange={handleLocaleChange} />
                    </Flex>

                    <Flex gap="8px" flexShrink={0} ml="auto" wrap="wrap" justifyContent="flex-end">
                        {tabIndex === 1 && (
                            <Button
                                _focus={{ outline: 0 }}
                                _hover={{}}
                                _active={{}}
                                onClick={onRebuildOpen}
                                variant="brand"
                                cursor="pointer"
                                whiteSpace="nowrap">
                                <Icon icon="trowel-bricks" /> &nbsp; Rebuild a sections Quiz library
                            </Button>
                        )}
                        {/* Create quiz button hidden from UI on Sections (CHAPTER) tab (code kept intentionally) */}
                        {tabIndex !== 1 && (
                            <Button
                                _focus={{ outline: 0 }}
                                _hover={{}}
                                _active={{}}
                                as={Link}
                                to={`/quiz/library/create?libraryType=${libraryType}`}
                                variant="brand"
                                cursor="pointer"
                                whiteSpace="nowrap">
                                <Icon icon="plus" /> &nbsp; Create a New Quiz
                            </Button>
                        )}
                    </Flex>
                </Flex>
            </Flex>

            <Flex flexDir="column">
                <Table bgColor="#ffffff">
                    {results.length > 0 && (
                        <Thead borderBottom="1px solid #e5ebf3">
                            <Tr w="100%">
                                <ThEl align="start" w="65%">Title</ThEl>
                                <ThEl w="10%" align="start">{tabIndex === 1 ? 'Fields' : 'Subjects'}</ThEl>
                                <ThEl w="15%">Actions</ThEl>
                            </Tr>
                        </Thead>
                    )}
                    <Tbody>
                        {results.length > 0 ? (
                            results.map((result) => (
                                <QuizLibraryItemV2
                                    key={result.id}
                                    result={result}
                                    isSectionType={tabIndex === 1}
                                />
                            ))
                        ) : (
                            <>
                                {!isFetching && (
                                    <Tr>
                                        <Td>
                                            <Box>No list items</Box>
                                        </Td>
                                    </Tr>
                                )}
                            </>
                        )}
                    </Tbody>
                </Table>

                {totalPages > 1 && (
                    <Flex justifyContent="end" mt="20px">
                        <Pagination
                            currentPage={page}
                            totalPages={totalPages}
                            handlePagination={setPage}
                        />
                    </Flex>
                )}
            </Flex>
        </Flex>
    );
};
